import { dateLabel } from '@mds/shared/lib/format';
import type {
  AdminSurvey,
  AdminSurveyResponse,
  SurveyDefinition,
  SurveyLanguage,
  SurveyQuestion,
} from '@mds/shared/types/survey';

import {
  CHANNEL_LABELS,
  LANGUAGE_LABELS,
  answerText,
  followUpKeys,
  followUpsOf,
  readQuestions,
  textIn,
} from '@/domain/surveys';
import { download, stamp } from '@/lib/export';
import * as surveys from '@/surveys/surveys.api';

/**
 * Survey responses as Excel workbooks: one response as a sheet of questions and answers, or
 * every response as one row each with a column per question. Written in the language being read.
 *
 * Built in the browser, so `exceljs` is loaded only when someone exports and never weighs on the
 * admin bundle. The PDFs are printed from the screen instead (`printDialog` in `lib/export.ts`).
 */

/* The brand palette as ARGB, traceable to `frontend/shared/src/styles/primitives.css`
 * and matching the feedback workbook (`events/feedback-export.ts`).
 * Excel has no tokens, so the literals live here, named for the primitive they copy. */
const MINT_DARK = 'FF168B70'; // --mint-700
const INK_SECONDARY = 'FF5B6472'; // --neutral-500
const HAIRLINE = 'FFDDE3EA'; // --line-100
const WHITE = 'FFFFFFFF';

/** The API's page cap for this list. */
const PAGE_LIMIT = 100;

/** Rows above the table: title, details, blank. */
const HEADER_ROW = 4;

const XLSX_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const L = {
  en: {
    sheet: 'Responses',
    responses: 'responses',
    exported: 'exported',
    question: 'Question',
    answer: 'Answer',
    filledIn: 'Filled in',
    channel: 'How it was collected',
    enteredIn: 'Entered in',
    enteredBy: 'Entered by',
    editedBy: 'Last edited by',
  },
  ar: {
    sheet: 'الإجابات',
    responses: 'إجابة',
    exported: 'تاريخ التصدير',
    question: 'السؤال',
    answer: 'الإجابة',
    filledIn: 'تاريخ التعبئة',
    channel: 'طريقة الجمع',
    enteredIn: 'أُدخلت بـ',
    enteredBy: 'أدخلها',
    editedBy: 'آخر تعديل بواسطة',
  },
} as const;

/** One answered question of a response, as it reads: a follow-up folds into the pick it explains. */
export interface AnswerRow {
  key: string;
  question: string;
  answer: string;
}

export function answerRows(
  response: AdminSurveyResponse,
  definition: SurveyDefinition,
  language: SurveyLanguage,
): AnswerRow[] {
  const followUps = followUpsOf(definition);
  const inline = followUpKeys(followUps);
  const rows: AnswerRow[] = [];
  for (const { question, number } of readQuestions(definition)) {
    if (inline.has(question.key)) continue;
    const answer = answerText(question, response.answers, followUps, language);
    if (!answer) continue;
    rows.push({
      key: question.key,
      question: `${number ? `${number}. ` : ''}${textIn(question.label, language)}`,
      answer,
    });
  }
  return rows;
}

/** Every response to a survey, oldest first page by page as the API hands them. */
export async function fetchAllResponses(key: string) {
  const rows: AdminSurveyResponse[] = [];
  for (let page = 1; ; page += 1) {
    const result = await surveys.listSurveyResponses(key, { page, limit: PAGE_LIMIT });
    rows.push(...result.data);
    if (page >= result.pagination.totalPages) return rows;
  }
}

/** The definition each response reads in: the version it was saved against. */
export async function definitionsFor(survey: AdminSurvey, responses: AdminSurveyResponse[]) {
  const definitions = new Map<number, SurveyDefinition>([[survey.version, survey.definition]]);
  const older = [...new Set(responses.map((r) => r.surveyVersion))].filter(
    (v) => !definitions.has(v),
  );
  const fetched = await Promise.all(older.map((v) => surveys.getSurveyVersion(survey.key, v)));
  for (const version of fetched) definitions.set(version.version, version.definition);
  return definitions;
}

/** A date key (`yyyy-mm-dd`) as the Excel date of that day. */
const excelDate = (key: string) => new Date(`${key}T00:00:00Z`);

async function workbook() {
  const { default: ExcelJS } = await import('exceljs');
  return new ExcelJS.Workbook();
}

type Sheet = ReturnType<Awaited<ReturnType<typeof workbook>>['addWorksheet']>;

function heading(sheet: Sheet, title: string, details: string) {
  const top = sheet.getCell('A1');
  top.value = title;
  top.font = { bold: true, size: 16, color: { argb: MINT_DARK } };
  const below = sheet.getCell('A2');
  below.value = details;
  below.font = { color: { argb: INK_SECONDARY } };
}

function headerRow(sheet: Sheet, values: string[]) {
  const header = sheet.getRow(HEADER_ROW);
  header.values = values;
  header.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: WHITE } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: MINT_DARK } };
    cell.alignment = { vertical: 'middle', wrapText: true };
  });
  header.height = 30;
}

function bodyRow(sheet: Sheet, values: Record<string, unknown> | unknown[]) {
  const row = sheet.addRow(values);
  row.alignment = { vertical: 'top', wrapText: true };
  row.eachCell({ includeEmpty: true }, (cell) => {
    cell.border = { bottom: { style: 'thin', color: { argb: HAIRLINE } } };
  });
  return row;
}

function sheetIn(book: Awaited<ReturnType<typeof workbook>>, language: SurveyLanguage) {
  return book.addWorksheet(L[language].sheet, {
    views: [{ state: 'frozen', ySplit: HEADER_ROW, rightToLeft: language === 'ar' }],
  });
}

/** One response: who filled it in and when, then every answer beside its question. */
export async function exportResponseXlsx(
  survey: AdminSurvey,
  response: AdminSurveyResponse,
  definition: SurveyDefinition,
  language: SurveyLanguage,
  title: string,
) {
  const l = L[language];
  const book = await workbook();
  const sheet = sheetIn(book, language);
  sheet.columns = [
    { key: 'question', width: 56 },
    { key: 'answer', width: 56 },
  ];

  heading(
    sheet,
    `${textIn(survey.definition.title, language)} — ${title}`,
    [
      `${l.filledIn} ${dateLabel(response.collectedOn)}`,
      CHANNEL_LABELS[response.channel][language],
      `${l.enteredIn} ${LANGUAGE_LABELS[response.language]}`,
      `${l.enteredBy} ${response.enteredBy.name}`,
      ...(response.updatedBy ? [`${l.editedBy} ${response.updatedBy.name}`] : []),
      `${l.exported} ${stamp()}`,
    ].join(' · '),
  );
  headerRow(sheet, [l.question, l.answer]);
  for (const row of answerRows(response, definition, language)) {
    bodyRow(sheet, { question: row.question, answer: row.answer });
  }

  download(
    `${survey.key}-response-${response.collectedOn}-${response.id.slice(0, 8)}.xlsx`,
    await book.xlsx.writeBuffer(),
    XLSX_TYPE,
  );
}

/**
 * Every response, one row each. The columns are the current questionnaire's, then any question
 * only an older version asked; each response is read in the words of the version it was saved in.
 */
export async function exportAllXlsx(
  survey: AdminSurvey,
  responses: AdminSurveyResponse[],
  definitions: Map<number, SurveyDefinition>,
  language: SurveyLanguage,
) {
  const l = L[language];
  const columns = new Map<string, string>();
  const ordered = [
    survey.definition,
    ...[...definitions].filter(([v]) => v !== survey.version).map(([, d]) => d),
  ];
  for (const definition of ordered) {
    const inline = followUpKeys(followUpsOf(definition));
    for (const { question, number } of readQuestions(definition)) {
      if (inline.has(question.key) || columns.has(question.key)) continue;
      const label = textIn(question.shortLabel, language) || textIn(question.label, language);
      columns.set(question.key, `${number ? `${number}. ` : ''}${label}`);
    }
  }

  const readers = new Map(
    [...definitions].map(([v, definition]) => [
      v,
      {
        byKey: new Map<string, SurveyQuestion>(
          readQuestions(definition).map(({ question }) => [question.key, question]),
        ),
        followUps: followUpsOf(definition),
      },
    ]),
  );

  const book = await workbook();
  const sheet = sheetIn(book, language);
  sheet.columns = [
    { key: 'filledIn', width: 14 },
    { key: 'channel', width: 18 },
    { key: 'enteredBy', width: 20 },
    ...[...columns.keys()].map((key) => ({ key, width: 32 })),
  ];

  heading(
    sheet,
    textIn(survey.definition.title, language),
    `${responses.length} ${l.responses} · ${l.exported} ${stamp()}`,
  );
  headerRow(sheet, [l.filledIn, l.channel, l.enteredBy, ...columns.values()]);

  for (const r of responses) {
    const reader = readers.get(r.surveyVersion) ?? readers.get(survey.version)!;
    const row = bodyRow(sheet, {
      filledIn: excelDate(r.collectedOn),
      channel: CHANNEL_LABELS[r.channel][language],
      enteredBy: r.enteredBy.name,
      ...Object.fromEntries(
        [...columns.keys()].map((key) => {
          const question = reader.byKey.get(key);
          return [key, question ? answerText(question, r.answers, reader.followUps, language) : ''];
        }),
      ),
    });
    row.getCell('filledIn').numFmt = 'dd/mm/yyyy';
  }

  if (responses.length > 0) {
    sheet.autoFilter = {
      from: { row: HEADER_ROW, column: 1 },
      to: { row: HEADER_ROW, column: 3 + columns.size },
    };
  }

  download(`${survey.key}-responses-${stamp()}.xlsx`, await book.xlsx.writeBuffer(), XLSX_TYPE);
}
